import { useCallback } from 'react';

import { CommunityNavArticle } from '@lumapps/articles/types';
import { Community } from '@lumapps/communities/types';
import { getCustomContentTypes } from '@lumapps/content-types/ducks/selectors';
import { isCommunity } from '@lumapps/content-types/utils';
import { Content } from '@lumapps/contents/types';
import { getGroupAll } from '@lumapps/customer/ducks/selectors';
import { getInstanceById } from '@lumapps/instance/ducks/selectors';
import { Media } from '@lumapps/medias/types';
import { getImageUrlFromContent, getMediaContent } from '@lumapps/medias/utils';
import { NavigationItemType, NavigationMapItem, NavigationMapPageReferenceType } from '@lumapps/navigation/types';
import { useSelector } from '@lumapps/redux/react';
import { translateToApiV2Format, useTranslate } from '@lumapps/translations';

import { generateNewNodeBase } from '../utils';

/** Check that the given entity is a content */
const entityIsContent = (
    entity: Content | Community | CommunityNavArticle,
    type: NavigationMapPageReferenceType,
): entity is Content =>
    Boolean(entity.id) &&
    (type === NavigationItemType.CONTENT ||
        type === NavigationItemType.COMMUNITY ||
        type === NavigationItemType.DIRECTORY ||
        type === NavigationItemType.SITE);

/** Check that the given entity is a nav article */
const entityIsNavArticle = (entity: any): entity is CommunityNavArticle => entity?.url;

export type ConvertEntityProps = {
    entity: Content | Community | CommunityNavArticle;
    type: NavigationMapPageReferenceType;
};

interface useConvertContentToNavigationNodeProps {
    languages: Array<string>;
    defaultLanguage: string;
}

/**
 * Hook to convert a given entity to its corresponding navigation item object.
 * Returns a conversion function to lazily convert.
 */
export const useConvertContentToNavigationNode = ({
    languages,
    defaultLanguage,
}: useConvertContentToNavigationNodeProps) => {
    const { translate } = useTranslate();
    const instanceById = useSelector(getInstanceById);
    const customContentTypes = useSelector(getCustomContentTypes);
    const allFeed = useSelector(getGroupAll);
    const convertEntity = useCallback(
        ({ entity, type }: ConvertEntityProps) => {
            /**
             * Create a default navigation item, common to all navigation item types
             */
            let partialNavigationItem: Partial<NavigationMapItem> = {
                pageReference: {
                    type,
                    id: entity.id,
                },
                visibleBy: allFeed ? [allFeed?.id] : [],
                title: entity.title ? translateToApiV2Format(entity.title, defaultLanguage, languages) : undefined,
            };

            /**
             * Add content specific values
             */
            if (entityIsContent(entity, type)) {
                const { customContentTypeDetails, customContentType, instance, mediaThumbnail } = entity;
                // Get content type data if available
                const {
                    id: contentTypeId,
                    name: contentTypeName,
                    icon: contentTypeIcon,
                } = customContentTypeDetails || {};

                // Build content type object if all information are available
                const contentTypeFromEntity =
                    contentTypeId && contentTypeName && contentTypeIcon
                        ? { id: contentTypeId, name: contentTypeName, icon: contentTypeIcon }
                        : undefined;

                // Use the custom content type in entity if available. If not, get the one from redux store.
                const contentType =
                    contentTypeFromEntity ||
                    (customContentType && customContentTypes?.[customContentType]) ||
                    undefined;

                // get the possible instance details from Redux store
                const siteReference = (instance && instanceById?.[instance]) || undefined;
                // Get content thumbnail
                const mediaContent = mediaThumbnail && getMediaContent(mediaThumbnail as Media, translate);
                const imageURL = mediaContent ? getImageUrlFromContent(mediaContent) : undefined;

                /** Add content specific attributes to navigation item */
                partialNavigationItem = {
                    ...partialNavigationItem,
                    type: NavigationItemType.PAGE,
                    pageReference: {
                        ...partialNavigationItem.pageReference,
                        // All "content" type pages (content / directories) must have "NavigationItemType.CONTENT" set,
                        // except for communities.
                        type: isCommunity(entity) ? NavigationItemType.COMMUNITY : NavigationItemType.CONTENT,
                        groupIds: entity.feedKeys,
                        privacy: isCommunity(entity) ? entity.privacy : undefined,
                    },
                    slug: entity.slug ? translateToApiV2Format(entity.slug || {}, defaultLanguage) : undefined,
                    siteReference,
                    contentType,
                    thumbnailUrl: imageURL,
                };
            }

            /**
             * Add article specific values
             */
            if (entityIsNavArticle(entity)) {
                partialNavigationItem = {
                    ...partialNavigationItem,
                    type: NavigationItemType.PAGE,
                    pageReference: {
                        ...partialNavigationItem.pageReference,
                        type: NavigationItemType.ARTICLE,
                    },
                    url: entity.url,
                };
            }
            return generateNewNodeBase(partialNavigationItem);
        },
        [allFeed, customContentTypes, defaultLanguage, instanceById, languages, translate],
    );

    return convertEntity;
};
